import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { Button, Space, type TableColumnsType, Tag, Typography } from 'antd'

import { ConfirmSwitch } from '@/components/common/ConfirmSwitch'
import { MONO_STYLE } from '@/components/common/MonoId'
import { formatUsdCents } from '@/utils/money'

type PaymentMethodDto = components['schemas']['PaymentMethodDto']

function formatFee(bps: number, fixedCents: number): string {
  const parts: string[] = []
  if (bps > 0) parts.push(`${(bps / 100).toFixed(2)}%`)
  if (fixedCents > 0) parts.push(formatUsdCents(fixedCents))
  return parts.length ? parts.join(' + ') : 'None'
}

interface Handlers {
  onToggle: (record: PaymentMethodDto, enabled: boolean) => Promise<void>
  onEdit: (record: PaymentMethodDto) => void
  onDelete: (record: PaymentMethodDto) => void
}

export function paymentMethodColumns({
  onToggle,
  onEdit,
  onDelete,
}: Handlers): TableColumnsType<PaymentMethodDto> {
  return [
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
    },
    {
      title: 'Method',
      dataIndex: 'methodCode',
      key: 'methodCode',
      render: (v: string) => (
        <Typography.Text copyable style={MONO_STYLE}>
          {v}
        </Typography.Text>
      ),
    },
    {
      title: 'Display name',
      dataIndex: 'displayName',
      key: 'displayName',
      ellipsis: true,
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      key: 'kind',
      width: 90,
      render: (k: string) => (
        <Tag color={k === 'crypto' ? 'geekblue' : 'gold'}>{k}</Tag>
      ),
    },
    {
      title: 'Min',
      dataIndex: 'minAmountCents',
      key: 'minAmountCents',
      width: 90,
      align: 'right',
      render: (c: number) => formatUsdCents(c),
    },
    {
      title: 'Max',
      dataIndex: 'maxAmountCents',
      key: 'maxAmountCents',
      width: 100,
      align: 'right',
      render: (c: number | null) =>
        c === null || c === undefined ? (
          <Typography.Text type="secondary">No cap</Typography.Text>
        ) : (
          formatUsdCents(c)
        ),
    },
    {
      title: 'Fee',
      key: 'fee',
      width: 130,
      render: (_: unknown, r: PaymentMethodDto) =>
        formatFee(r.estFeeBps, r.estFeeFixedCents),
    },
    {
      title: 'Fee paid by',
      dataIndex: 'feePaidByUser',
      key: 'feePaidByUser',
      width: 110,
      render: (byUser: boolean) => <Tag>{byUser ? 'User' : 'Platform'}</Tag>,
    },
    {
      title: 'Sort',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 70,
      align: 'right',
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (enabled: boolean, record: PaymentMethodDto) => (
        <ConfirmSwitch
          checked={enabled}
          target={`${record.provider} · ${record.methodCode}`}
          onLabel="Enable"
          offLabel="Disable"
          onConfirm={(checked) => onToggle(record, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, record: PaymentMethodDto) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(record)}
          />
        </Space>
      ),
    },
  ]
}
